"use client";

import { useState } from "react";
import { Send, CheckCircle } from "lucide-react";

type Service = { id: string; name: string };

export default function ContactForm({ services }: { services: Service[] }) {
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("sending");
    setError("");

    const form = e.currentTarget;
    const data = {
      serviceId:  (form.elements.namedItem("serviceId")  as HTMLSelectElement).value,
      firstName:  (form.elements.namedItem("firstName")  as HTMLInputElement).value,
      lastName:   (form.elements.namedItem("lastName")   as HTMLInputElement).value,
      phone:      (form.elements.namedItem("phone")      as HTMLInputElement).value,
      email:      (form.elements.namedItem("email")      as HTMLInputElement).value,
      subject:    (form.elements.namedItem("subject")    as HTMLInputElement).value,
      message:    (form.elements.namedItem("message")    as HTMLTextAreaElement).value,
    };

    const res = await fetch("/api/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });

    if (res.ok) {
      setStatus("sent");
    } else {
      const json = await res.json().catch(() => ({}));
      setError(json.error ?? "Une erreur est survenue. Veuillez réessayer.");
      setStatus("error");
    }
  }

  if (status === "sent") {
    return (
      <div className="flex flex-col items-center gap-4 rounded-2xl border border-green-200 bg-green-50 p-10 text-center">
        <CheckCircle size={40} className="text-green-500" />
        <div>
          <p className="text-lg font-semibold text-green-900">Message envoyé !</p>
          <p className="mt-1 text-sm text-green-700">
            Nous avons bien reçu votre demande. Un accusé de réception vous a été envoyé par email.
          </p>
        </div>
      </div>
    );
  }

  const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-100";
  const labelCls = "mb-1.5 block text-sm font-medium text-slate-700";

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Service */}
      <div>
        <label htmlFor="serviceId" className={labelCls}>Service concerné *</label>
        <select id="serviceId" name="serviceId" required className={inputCls}>
          <option value="">— Sélectionner un service —</option>
          {services.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {/* Nom / Prénom */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="firstName" className={labelCls}>Prénom *</label>
          <input id="firstName" name="firstName" type="text" required className={inputCls} />
        </div>
        <div>
          <label htmlFor="lastName" className={labelCls}>Nom *</label>
          <input id="lastName" name="lastName" type="text" required className={inputCls} />
        </div>
      </div>

      {/* Email / Téléphone */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="email" className={labelCls}>Adresse e-mail *</label>
          <input id="email" name="email" type="email" required className={inputCls} />
        </div>
        <div>
          <label htmlFor="phone" className={labelCls}>Téléphone</label>
          <input id="phone" name="phone" type="tel" className={inputCls} />
        </div>
      </div>

      {/* Motif */}
      <div>
        <label htmlFor="subject" className={labelCls}>Motif *</label>
        <input id="subject" name="subject" type="text" required className={inputCls} placeholder="Ex : Demande d'information, Signalement…" />
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className={labelCls}>Votre message *</label>
        <textarea id="message" name="message" rows={5} required className={inputCls} />
      </div>

      {error && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={status === "sending"}
          className="btn-shimmer inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-medium text-white transition hover:scale-105 active:scale-95 disabled:opacity-60"
          style={{ backgroundColor: "var(--brand)" }}
        >
          <Send size={15} />
          {status === "sending" ? "Envoi en cours…" : "Envoyer le message"}
        </button>
      </div>
    </form>
  );
}
